import type { ApiResponse } from '@evi/shared';
import { API_BASE_URL, api } from './api';

/**
 * EVI's pay pages for a New job (Api: routes/public/job-pay.ts; Warren, ruling A, 21 October 2026). No sign-in: the token
 * in the link is the key. Each Pay click asks the Api for a fresh Stripe Checkout Session, so the emailed link never
 * expires the way a raw Checkout link does.
 */
export interface CustomerPayView {
  partner: { name: string; email: string | null; phone: string | null; has_logo: boolean };
  customer: { business_name: string; contact_name: string };
  site: { street: string; city: string; state: string }; home: boolean;
  state: 'payable' | 'failed' | 'clearing' | 'paid' | 'refunded' | 'closed' | 'waiting'; message: string | null;
  lines: Array<{ name: string; quantity: number; unit_cents: number; total_cents: number }>;
  freight: { line: string; price_cents: number | null; note?: string } | null;
  total_cents: number; currency: string; tax_note: string | null;
  network: { cadence: 'annual' | 'monthly'; chargers: number; per_charger_cents: number | null; currency: string; line: string } | null;
  methods: Array<'us_bank_account' | 'card'>; po_number: boolean; labour: string;
}
export interface FreightPayView {
  partner: { name: string; has_logo: boolean }; title: string; order_number: string; ship_to: string; line: string; price_cents: number | null; notes: string | null;
  state: 'payable' | 'paid' | 'closed';
}
const tokenPath = (base: 'job-pay' | 'freight-pay', token: string, rest = '') => `public/${base}/${encodeURIComponent(token)}${rest}`;
export const customerPayLogo = (token: string) => `${API_BASE_URL}/${tokenPath('job-pay', token, '/logo')}`;

async function post(path: string): Promise<string> {
  try {
    return (await api.post(path, { json: {} }).json<ApiResponse<{ url: string }>>()).data!.url;
  } catch (err) {
    const response = (err as { response?: Response }).response;
    const b = response ? await response.json().catch(() => null) as { error?: { message?: string } } | null : null;
    throw new Error(b?.error?.message ?? 'The payment page did not open. Try again in a minute.');
  }
}
export const getCustomerPay = (token: string) => api.get(tokenPath('job-pay', token)).json<ApiResponse<CustomerPayView>>().then(r => r.data!);
/** A fresh Stripe Checkout Session for this order, on each click. */
export const startCustomerPay = (token: string) => post(tokenPath('job-pay', token, '/checkout'));
export const getFreightPay = (token: string) => api.get(tokenPath('freight-pay', token)).json<ApiResponse<FreightPayView>>().then(r => r.data!);
export const startFreightPay = (token: string) => post(tokenPath('freight-pay', token, '/checkout'));
export const usd = (cents: number) => `US$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
